// fieldStyles — the review lanes' input and label classes, in one place.
//
// Six files carried their own `const inputClass` / `const labelClass`, and four
// of the six strings were byte-identical. They are here so a change to how a
// flagged field looks happens once, and so the difference between two variants
// is visible as a difference instead of a diff.
//
// ── THE FILLED FIELD ─────────────────────────────────────────────────────────
// Every export below is one shape: a FILLED field on the top surface tone, no
// border at all. Depth comes from the step between the field and whatever it
// sits on (docs/operations/style-bible.md, "The design language"), which is why
// a bordered box is not just a different look but the wrong mechanism: two
// elevation systems on one screen read as neither.
//
// The placeholder is `placeholder:text-on-surface-variant`, FLAT, with no slash
// modifier. Two things about that are easy to get wrong:
//
// 1. `/60` on a surface role compiles to nothing. The roles are CSS custom
//    properties, and Tailwind 3 drops a slash modifier on those entirely rather
//    than failing, so the utility emits no rule at all and the placeholder falls
//    back to whatever the browser wanted.
// 2. Reaching for a dimmer step instead is not the fix either. gray-500
//    (#6b7280) on this field's surface-container-high fill measures 2.65:1,
//    well under the 4.5:1 a placeholder needs to be read. The flat role measures
//    6.68:1 on the same fill. A placeholder nobody can read is a label that is
//    not there.
//
// ── THE VARIANTS ARE PADDING NOW, AND ONLY PADDING ───────────────────────────
// Three densities survive because three real differences do. The fourth
// difference did not survive: see `laneInputRinged`.

/**
 * Color, focus and motion, with no shape and no padding: what a filled field IS
 * before a surface decides how it sits. Exported for the one place that needs a
 * different shape (the filter bar's pill search and selects, `FilterBar.tsx`),
 * so a pill field and a rectangular one can never disagree about tone or focus.
 */
export const fieldBase =
  'bg-surface-container-high shadow-recess text-sm text-on-surface '
  + 'placeholder:text-on-surface-variant focus:bg-surface-container-highest focus:outline-none '
  + 'focus:ring-2 focus:ring-primary/40 '
  + 'transition-[box-shadow,background-color] duration-150 ease-emphasized motion-reduce:transition-none';

/** The rectangular field. Padding is the variable. */
const FIELD_BASE = `w-full rounded-md ${fieldBase}`;

/**
 * The bench field: scan-to-stock and quote-to-draft-PO.
 *
 * `py-2 sm:py-1.5` is the whole reason this differs from the import card's.
 * Both flows now happen on a phone at the receiving bench, and 1.5 gives a
 * ~34px control — under a thumb, that is a miss waiting to happen. Desktop
 * density is unchanged.
 */
export const laneInput = `${FIELD_BASE} px-2.5 py-2 sm:py-1.5`;

/**
 * The same bench field: catalog import and packing list.
 *
 * This used to be the one lane variant that carried a keyboard-focus ring,
 * because the other two drew a colored BORDER on focus instead and the two
 * treatments could not both be right. The border is gone, so the ring is now
 * how every field shows focus and this string is `laneInput`.
 *
 * Kept as its own export rather than deleted: the four call sites are not part
 * of this change, and an alias states the collapse where a find-and-replace
 * would have hidden it.
 */
export const laneInputRinged = laneInput;

/**
 * The import card's field: desk density, no thumb bump.
 *
 * Deliberate. That lane files four years of paperwork at a desk, ~190 documents
 * in one sitting, and the taller control costs a screenful of rows.
 */
export const importCardInput = `${FIELD_BASE} px-2.5 py-1.5`;

/** The new-item modal's field: roomier padding, matching its own form rhythm. */
export const modalInput = `${FIELD_BASE} px-3 py-2`;

/**
 * The filled SELECT, for the same modal shape. index.css strips the native
 * chevron from every select app-wide, so a select styled with `modalInput`
 * alone is pixel-identical to a text box, and a required dropdown that looks
 * like a text field reads as filled in when it is empty. `field-select`
 * (index.css) draws the chevron the filter bar's pills draw, and `pr-9` keeps
 * the value clear of it. Callers add `text-on-surface-variant` while the value
 * is empty so the prompt reads in placeholder tone, not value tone.
 */
export const modalSelect = `${FIELD_BASE} field-select appearance-none cursor-pointer px-3 py-2 pr-9`;
export const laneSelect = `${FIELD_BASE} field-select appearance-none cursor-pointer px-2.5 py-2 sm:py-1.5 pr-8`;

/**
 * A field showing a value nobody may type into.
 *
 * One step DOWN from the editable tone: on a form where every writable field is
 * the brightest surface, a read-only one has to be quieter or it reads as a
 * control that is refusing to work.
 *
 * It KEEPS a keyboard focus indicator, and that is not an oversight to tidy up
 * later. A read-only input is still tabbable and its text is still selectable,
 * so somebody arrowing through a form has to be able to see where they are. The
 * focus-VISIBLE ring rather than the focus ring, so a pointer landing on it
 * stays silent while the keyboard path does not.
 */
export const readOnlyInput =
  'w-full bg-surface-container-high rounded-md px-3 py-2 text-sm text-on-surface-variant '
  + 'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50';

/** The four lanes' and the import card's field label. */
export const laneLabel =
  'block text-[10px] uppercase tracking-wider text-on-surface-variant mb-0.5';

/** The new-item modal's label: a step larger, on a roomier form. */
export const modalLabel =
  'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';
